import React, { forwardRef } from 'react';

import { domDataAttributes } from '@aline/common';
import clsx from 'clsx';
import { Building, UserRound } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';

// width/height ratio (36/20)
const ASPECT_RATIO = 1.8;
// indicator size as ratio of height (14/20)
const INDICATOR_SIZE_RATIO = 0.7;

const useStyles = makeStyles<{ height: number; toggled: boolean; disabled?: boolean }>()(
  (theme, { disabled, height, toggled }) => ({
    toggleBar: {
      backgroundColor: disabled || !toggled ? theme.appColors.rule : theme.appColors.primary,
      borderRadius: height / 2,
      cursor: disabled ? 'default' : 'pointer',
      height,
      position: 'relative',
      transition: 'background-color .2s ease',
      width: height * ASPECT_RATIO,
    },
    toggleIndicator: (() => {
      const indicatorSize = height * INDICATOR_SIZE_RATIO;
      const marginTopBottom = (height - indicatorSize) / 2;

      return {
        backgroundColor: 'white',
        borderRadius: '50%',
        height: indicatorSize,
        marginTop: marginTopBottom,
        position: 'relative' as const,
        transition: 'margin-left 0.2s ease',
        width: indicatorSize,
        zIndex: 1,
      };
    })(),
    toggleIndicatorLeft: (() => {
      const indicatorSize = height * INDICATOR_SIZE_RATIO;

      return {
        marginLeft: (height - indicatorSize) / 2,
      };
    })(),
    toggleIndicatorRight: (() => {
      const indicatorSize = height * INDICATOR_SIZE_RATIO;
      const marginLeftRight = (height - indicatorSize) / 2;
      const totalWidth = height * ASPECT_RATIO;

      return {
        marginLeft: totalWidth - indicatorSize - marginLeftRight,
      };
    })(),
  })
);

const useStylesWithIcon = makeStyles<{ height: number; toggled: boolean }>()(
  (theme, { height, toggled }) => ({
    iconContainer: {
      height: '100%',
      pointerEvents: 'none',
      position: 'absolute',
      top: 0,
      width: '100%',
      zIndex: 2,
    },
    iconLeft: (() => {
      const indicatorSize = height * INDICATOR_SIZE_RATIO;
      const marginLeftRight = (height - indicatorSize) / 2;
      const iconCenterLeft = marginLeftRight + indicatorSize / 2;

      return {
        color: toggled ? 'white' : theme.appColors.iconsType,
        left: iconCenterLeft,
        position: 'absolute' as const,
        top: '50%',
        transform: 'translate(-50%, -50%)',
      };
    })(),
    iconRight: (() => {
      const indicatorSize = height * INDICATOR_SIZE_RATIO;
      const marginLeftRight = (height - indicatorSize) / 2;
      const totalWidth = height * ASPECT_RATIO;
      const iconCenterRight = totalWidth - marginLeftRight - indicatorSize / 2;

      return {
        color: toggled ? theme.appColors.iconsType : 'white',
        left: iconCenterRight,
        position: 'absolute' as const,
        top: '50%',
        transform: 'translate(-50%, -50%)',
      };
    })(),
  })
);

interface ToggleSwitchProps extends React.HTMLAttributes<HTMLDivElement> {
  toggle: (e: React.MouseEvent) => void;
  toggled: boolean;
  children?: React.ReactNode;
  dataCy?: string;
  disabled?: boolean;
  height?: number;
}

export const ToggleSwitch = forwardRef(function ToggleSwitch(
  {
    children,
    className,
    dataCy,
    disabled,
    height = 20,
    toggle,
    toggled,
    ...tooltipProps
  }: ToggleSwitchProps,
  ref: React.Ref<HTMLDivElement>
): JSX.Element {
  const { classes } = useStyles({ disabled, height, toggled });
  const attributes = {
    [domDataAttributes.value]: `${toggled}`,
    [domDataAttributes.disabled]: `${disabled}`,
  };

  return (
    <div
      ref={ref}
      className={clsx(className, classes.toggleBar)}
      data-cy={dataCy}
      onClick={e => {
        if (!disabled) toggle(e);
      }}
      {...tooltipProps}
      {...attributes}
    >
      {children}
      <div
        className={clsx(
          classes.toggleIndicator,
          toggled && classes.toggleIndicatorRight,
          !toggled && classes.toggleIndicatorLeft
        )}
      />
    </div>
  );
});

interface ToggleSwitchWithIconProps extends React.HTMLAttributes<HTMLDivElement> {
  toggle: (e: React.MouseEvent) => void;
  toggled: boolean;
  dataCy?: string;
  disabled?: boolean;
  height?: number;
}

export const ToggleSwitchWithIcon = forwardRef(function ToggleSwitchWithIcon(
  {
    className,
    dataCy,
    disabled,
    height = 24,
    toggle,
    toggled,
    ...props
  }: ToggleSwitchWithIconProps,
  ref: React.Ref<HTMLDivElement>
): JSX.Element {
  const { classes } = useStylesWithIcon({ height, toggled });
  // Icons are 50% of the toggle height (10/20 = 0.5).
  const iconSize = height * 0.5;

  return (
    <ToggleSwitch
      ref={ref}
      className={className}
      dataCy={dataCy}
      disabled={disabled}
      height={height}
      toggle={toggle}
      toggled={toggled}
      {...props}
    >
      <div className={classes.iconContainer}>
        <UserRound className={classes.iconLeft} size={iconSize} />
        <Building className={classes.iconRight} size={iconSize} />
      </div>
    </ToggleSwitch>
  );
});
